import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  colLabel, feetBetween, isRevealed, squareLabel, MAP_MAX_SIZE, MAP_MIN_SIZE,
  type BattleMap, type MapResponse, type MapToken,
} from "@dm/shared";
import { mapApi } from "./api.js";
import { useGame } from "./game.js";

/** Keep pointer events on the element while dragging (can throw if the pointer is already gone). */
function capture(e: React.PointerEvent): void {
  try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch { /* fine: moves still arrive while over the element */ }
}

type Tool = "move" | "measure" | "reveal" | "hide" | "wall" | "unwall";
type Cell = { x: number; y: number };

/**
 * Phase 7: the battle map. A 5 ft square grid over an optional picture, tokens for the party and the fight (synced live),
 * fog of war, a measuring tape, and Host tools for scenes, pictures and extra tokens. Works with touch: drag a token
 * with a finger; the page itself still scrolls outside the tokens.
 */
export function MapPage() {
  const { campaign, user, isHost, mapVersion, combat, party, fail } = useGame();
  const [data, setData] = useState<MapResponse | null>(null);
  const [tool, setTool] = useState<Tool>("move");
  const [zoom, setZoom] = useState(1);
  const [width, setWidth] = useState(800);
  const [selected, setSelected] = useState<number | null>(null);
  const wrap = useRef<HTMLDivElement>(null);

  const load = useCallback(() => mapApi.get(campaign.id).then(setData, fail), [campaign.id, fail]);
  // Live: refetch when the map changes, the fight changes (monsters, turns, HP) or the party changes.
  const turnKey = combat ? `${combat.id}:${combat.currentCombatantId}:${combat.combatants.map((c) => `${c.id}${c.down ? "d" : ""}`).join(",")}` : "none";
  const partyKey = party.map((c) => `${c.id}:${c.name}:${c.currentHp === 0 ? 0 : 1}`).join(",");
  useEffect(() => { void load(); }, [load, mapVersion, turnKey, partyKey]);

  useEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setWidth(el.clientWidth));
    ro.observe(el);
    setWidth(el.clientWidth);
    return () => ro.disconnect();
  }, [data?.map?.id]);

  if (!data) return <main className="map-page"><p className="muted">Loading the map…</p></main>;
  if (!data.enabled) return <main className="map-page"><p className="muted">The battle map is off for this campaign. A Host can turn it on in Campaign settings.</p></main>;

  const map = data.map;
  // Fit the width, but keep squares big enough to touch on a phone (the map then scrolls sideways).
  const minCell = width < 600 ? 30 : 20;
  const cell = map ? Math.max(minCell, Math.min(64, Math.floor((width - 2) / map.cols))) * zoom : 32;
  const current = combat?.combatants.find((c) => c.id === data.currentCombatantId) ?? null;

  return (
    <main className="map-page">
      <div className="map-head">
        <h2>{map ? map.name : "Battle map"}</h2>
        {map?.imageStatus === "pending" && <span className="muted map-painting" aria-live="polite">Painting the map…</span>}
        {map?.byDm && data.canEdit && <span className="muted">made by the DM</span>}
        {combat && (
          <span className="map-turn" aria-live="polite">
            {combat.currentCombatantId === null ? "Rolling initiative" : `Round ${combat.round}`}{current ? ` · ${current.name}'s turn` : ""}
          </span>
        )}
      </div>
      {map ? (
        <>
          <div className="map-toolbar" role="toolbar" aria-label="Map tools">
            <ToolButton tool="move" cur={tool} set={setTool} label="Move" hint="Drag a token to move it" />
            <ToolButton tool="measure" cur={tool} set={setTool} label="Measure" hint="Drag from one square to another" />
            {data.canEdit && <ToolButton tool="reveal" cur={tool} set={setTool} label="Reveal" hint="Drag an area to clear the fog" />}
            {data.canEdit && <ToolButton tool="hide" cur={tool} set={setTool} label="Fog" hint="Drag an area to cover it" />}
            {data.canEdit && <ToolButton tool="wall" cur={tool} set={setTool} label="Walls" hint="Drag to paint walls or impassable squares (monsters path around them)" />}
            {data.canEdit && <ToolButton tool="unwall" cur={tool} set={setTool} label="Erase" hint="Drag to erase walls" />}
            <span className="map-zoom">
              <button type="button" aria-label="Zoom out" onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.25).toFixed(2)))}>−</button>
              <button type="button" aria-label="Zoom in" onClick={() => setZoom((z) => Math.min(2.5, +(z + 0.25).toFixed(2)))}>+</button>
            </span>
          </div>
          <div className="map-scroll" ref={wrap}>
            <Board
              key={map.id} map={map} tokens={data.tokens} cell={cell} tool={tool} canEdit={data.canEdit} me={user.discordId}
              currentCombatantId={data.currentCombatantId} selected={selected} onSelect={setSelected}
              onMove={(t, x, y) => mapApi.move(campaign.id, t.id, { x, y }).then(setData, (e) => { fail(e); void load(); })}
              onFog={(mode, rect) => mapApi.fog(campaign.id, map.id, mode, rect).then(setData, fail)}
              onWalls={(mode, rect) => mapApi.walls(campaign.id, map.id, mode, rect).then(setData, fail)}
            />
          </div>
          <Legend tokens={data.tokens} me={user.discordId} canEdit={data.canEdit} />
        </>
      ) : (
        <p className="muted">{isHost ? "No map yet: make one below. The party's tokens appear on it right away, and monsters join when a fight starts." : "No map yet. The Host or the DM will set one up when it's needed."}</p>
      )}
      {data.canEdit && <HostTools data={data} setData={setData} selected={selected} setSelected={setSelected} />}
    </main>
  );
}

function ToolButton({ tool, cur, set, label, hint }: { tool: Tool; cur: Tool; set: (t: Tool) => void; label: string; hint: string }) {
  return <button type="button" className={cur === tool ? "active" : ""} aria-pressed={cur === tool} title={hint} onClick={() => set(tool)}>{label}</button>;
}

interface BoardProps {
  map: BattleMap;
  tokens: MapToken[];
  cell: number;
  tool: Tool;
  canEdit: boolean;
  me: string;
  currentCombatantId: number | null;
  selected: number | null;
  onSelect: (id: number | null) => void;
  onMove: (t: MapToken, x: number, y: number) => void;
  onFog: (mode: "reveal" | "hide", rect: [number, number, number, number]) => void;
  onWalls: (mode: "paint" | "erase", rect: [number, number, number, number]) => void;
}

function Board({ map, tokens, cell, tool, canEdit, me, currentCombatantId, selected, onSelect, onMove, onFog, onWalls }: BoardProps) {
  const board = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<{ id: number; from: Cell; to: Cell; dx: number; dy: number } | null>(null);
  const [span, setSpan] = useState<{ from: Cell; to: Cell } | null>(null);
  // Optimistic positions until the server's answer arrives.
  const [pending, setPending] = useState<Record<number, Cell>>({});
  useEffect(() => { setPending({}); }, [tokens]);

  const W = map.cols * cell, H = map.rows * cell;
  const cellAt = (e: { clientX: number; clientY: number }): Cell => {
    const r = board.current!.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(map.cols - 1, Math.floor((e.clientX - r.left) / cell))),
      y: Math.max(0, Math.min(map.rows - 1, Math.floor((e.clientY - r.top) / cell))),
    };
  };
  const mayMove = (t: MapToken) => canEdit || (t.kind === "pc" && t.ownerDiscordId === me);

  const onTokenDown = (e: React.PointerEvent, t: MapToken) => {
    if (tool !== "move") return;
    onSelect(t.id);
    if (!mayMove(t)) return;
    e.preventDefault();
    e.stopPropagation();
    capture(e);
    const c = cellAt(e);
    setDrag({ id: t.id, from: { x: t.x, y: t.y }, to: { x: t.x, y: t.y }, dx: c.x - t.x, dy: c.y - t.y });
  };
  const onTokenMove = (e: React.PointerEvent) => {
    if (!drag) return;
    const c = cellAt(e);
    const t = tokens.find((x) => x.id === drag.id)!;
    const to = {
      x: Math.max(0, Math.min(map.cols - t.size, c.x - drag.dx)),
      y: Math.max(0, Math.min(map.rows - t.size, c.y - drag.dy)),
    };
    if (to.x !== drag.to.x || to.y !== drag.to.y) setDrag({ ...drag, to });
  };
  const onTokenUp = () => {
    if (!drag) return;
    const t = tokens.find((x) => x.id === drag.id);
    const d = drag;
    setDrag(null);
    if (t && (d.to.x !== d.from.x || d.to.y !== d.from.y)) {
      setPending((p) => ({ ...p, [t.id]: d.to }));
      onMove(t, d.to.x, d.to.y);
    }
  };

  const onBoardDown = (e: React.PointerEvent) => {
    if (tool === "move") { onSelect(null); return; }
    e.preventDefault();
    capture(e);
    const c = cellAt(e);
    setSpan({ from: c, to: c });
  };
  const onBoardMove = (e: React.PointerEvent) => {
    if (!span) return;
    const c = cellAt(e);
    if (c.x !== span.to.x || c.y !== span.to.y) setSpan({ ...span, to: c });
  };
  const onBoardUp = () => {
    if (!span) return;
    const s = span;
    if (tool === "reveal" || tool === "hide" || tool === "wall" || tool === "unwall") {
      const x = Math.min(s.from.x, s.to.x), y = Math.min(s.from.y, s.to.y);
      const rect: [number, number, number, number] = [x, y, Math.abs(s.from.x - s.to.x) + 1, Math.abs(s.from.y - s.to.y) + 1];
      if (tool === "wall" || tool === "unwall") onWalls(tool === "wall" ? "paint" : "erase", rect); else onFog(tool, rect);
      setSpan(null);
    } else {
      // Measure: keep the line on screen until the next measurement.
      setSpan({ ...s });
    }
  };
  useEffect(() => { setSpan(null); }, [tool]);

  // Fog: players see black outside revealed areas; the Host sees it shaded.
  const fogMask = map.fog ? (
    <svg className="map-fog" width={W} height={H} aria-hidden="true">
      <defs>
        <mask id={`fog-${map.id}`}>
          <rect width={W} height={H} fill="white" />
          {map.revealed.map(([x, y, w, h], i) => <rect key={i} x={x * cell} y={y * cell} width={w * cell} height={h * cell} fill="black" />)}
        </mask>
      </defs>
      <rect width={W} height={H} fill={canEdit ? "rgba(10,10,20,0.55)" : "rgb(12,12,18)"} mask={`url(#fog-${map.id})`} />
    </svg>
  ) : null;

  const moving = drag ? tokens.find((t) => t.id === drag.id) : null;
  const sel = span && (tool === "reveal" || tool === "hide" || tool === "wall" || tool === "unwall") ? {
    x: Math.min(span.from.x, span.to.x), y: Math.min(span.from.y, span.to.y),
    w: Math.abs(span.from.x - span.to.x) + 1, h: Math.abs(span.from.y - span.to.y) + 1,
  } : null;

  return (
    <div
      ref={board} className={`map-board tool-${tool}`} style={{ width: W, height: H }}
      onPointerDown={onBoardDown} onPointerMove={onBoardMove} onPointerUp={onBoardUp} onPointerCancel={() => setSpan(null)}
      role="application" aria-label={`Battle map, ${map.cols} by ${map.rows} squares`}
    >
      {map.imageUrl ? <img className="map-bg" src={map.imageUrl} alt="" draggable={false} /> : <div className="map-bg plain" />}
      <svg className="map-grid" width={W} height={H} aria-hidden="true">
        <defs>
          <pattern id={`grid-${map.id}`} width={cell} height={cell} patternUnits="userSpaceOnUse">
            <path d={`M ${cell} 0 L 0 0 0 ${cell}`} fill="none" stroke="rgba(0,0,0,0.35)" strokeWidth="1" />
          </pattern>
        </defs>
        <rect width={W} height={H} fill={`url(#grid-${map.id})`} />
        {(map.blocked ?? []).length > 0 && (!map.imageUrl || canEdit) && (map.blocked ?? []).map(([x, y]) => (
          <rect key={`b${x},${y}`} className={map.imageUrl ? "map-blocked faint" : "map-blocked"} x={x * cell} y={y * cell} width={cell} height={cell} />
        ))}
      </svg>
      {cell >= 26 && <Labels map={map} cell={cell} />}
      {fogMask}
      {tokens.map((t) => {
        // Small cells (phones): names only for the selected token, the current turn and your own, so labels don't pile up.
        const showName = (x: MapToken) => cell * x.size >= 30 && (cell >= 44 || x.size > 1 || selected === x.id || x.ownerDiscordId === me
          || (x.combatantId !== null && x.combatantId === currentCombatantId));
        const p = drag?.id === t.id ? drag.to : pending[t.id] ?? { x: t.x, y: t.y };
        const fogged = canEdit && t.kind !== "pc" && !isRevealed(map, t.x, t.y);
        const cls = ["map-token", `k-${t.kind}`, t.down ? "down" : "", t.hidden || fogged ? "ghost" : "", selected === t.id ? "selected" : "",
          t.combatantId !== null && t.combatantId === currentCombatantId ? "current" : "", mayMove(t) ? "movable" : "", drag?.id === t.id ? "dragging" : ""].filter(Boolean).join(" ");
        const size = t.size * cell;
        return (
          <div
            key={t.id} className={cls} style={{ left: p.x * cell, top: p.y * cell, width: size, height: size }}
            onPointerDown={(e) => onTokenDown(e, t)} onPointerMove={onTokenMove} onPointerUp={onTokenUp} onPointerCancel={() => setDrag(null)}
            title={`${t.name} (${squareLabel(p.x, p.y)})${t.hidden ? " · hidden from players" : ""}${t.down ? " · down" : ""}`}
            aria-label={`${t.name} at ${squareLabel(p.x, p.y)}`}
          >
            {t.avatarUrl ? <img src={t.avatarUrl} alt="" draggable={false} /> : <span className="initials" style={{ fontSize: Math.max(10, size * 0.32) }}>{initials(t.name)}</span>}
            {showName(t) && <span className="map-name" style={{ maxWidth: Math.max(size + 16, Math.min(140, cell * 2.2)) }}>{t.name}</span>}
          </div>
        );
      })}
      {drag && moving && (drag.to.x !== drag.from.x || drag.to.y !== drag.from.y) && (
        <Ruler from={drag.from} to={drag.to} cell={cell} label={`${feetBetween(drag.from, drag.to)} ft`} />
      )}
      {span && tool === "measure" && <Ruler from={span.from} to={span.to} cell={cell} label={`${feetBetween(span.from, span.to)} ft`} />}
      {sel && <div className={`map-select ${tool}`} style={{ left: sel.x * cell, top: sel.y * cell, width: sel.w * cell, height: sel.h * cell }} />}
    </div>
  );
}

function Labels({ map, cell }: { map: BattleMap; cell: number }) {
  return (
    <div className="map-labels" aria-hidden="true">
      {Array.from({ length: map.cols }, (_, x) => <span key={`c${x}`} style={{ left: x * cell + 2, top: 1 }}>{colLabel(x)}</span>)}
      {Array.from({ length: map.rows }, (_, y) => y > 0 && <span key={`r${y}`} style={{ left: 2, top: y * cell + 1 }}>{y + 1}</span>)}
    </div>
  );
}

function Ruler({ from, to, cell, label }: { from: Cell; to: Cell; cell: number; label: string }) {
  const c = (v: number) => v * cell + cell / 2;
  return (
    <>
      <svg className="map-ruler" aria-hidden="true">
        <line x1={c(from.x)} y1={c(from.y)} x2={c(to.x)} y2={c(to.y)} />
        <circle cx={c(from.x)} cy={c(from.y)} r={4} />
        <circle cx={c(to.x)} cy={c(to.y)} r={4} />
      </svg>
      <span className="map-ruler-label" role="status" style={{ left: c(to.x) + 8, top: c(to.y) - 26 }}>{label}</span>
    </>
  );
}

function Legend({ tokens, me, canEdit }: { tokens: MapToken[]; me: string; canEdit: boolean }) {
  const mine = tokens.find((t) => t.kind === "pc" && t.ownerDiscordId === me);
  return (
    <p className="muted small map-legend">
      {canEdit
        ? "Drag any token to move it. Tap a token to select it for the tools below. Shaded areas are fogged for players."
        : mine ? `Drag ${mine.name} to move. Use Measure to check a distance (5 ft squares, diagonals count 5 ft).` : "Use Measure to check a distance (5 ft squares)."}
      <span className="map-key"><i className="k-pc" /> party <i className="k-monster" /> monsters <i className="k-npc" /> others <i className="k-current" /> current turn</span>
    </p>
  );
}

function initials(name: string): string {
  const w = name.split(/\s+/).filter(Boolean);
  const num = w.length > 1 && /^\d+$/.test(w[w.length - 1]!) ? w.pop()! : "";
  return (w.slice(0, 2).map((x) => x[0]!.toUpperCase()).join("") + num).slice(0, 3) || "?";
}

function HostTools({ data, setData, selected, setSelected }: { data: MapResponse; setData: (d: MapResponse) => void; selected: number | null; setSelected: (n: number | null) => void }) {
  const { campaign, fail } = useGame();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", cols: 24, rows: 16 });
  const [prompt, setPrompt] = useState("");
  const [npc, setNpc] = useState({ name: "", size: 1, hidden: false });
  const map = data.map;
  const run = async (f: () => Promise<MapResponse>) => {
    setBusy(true);
    try { setData(await f()); } catch (err) { fail(err); } finally { setBusy(false); }
  };
  const sel = useMemo(() => data.tokens.find((t) => t.id === selected) ?? null, [data.tokens, selected]);
  const upload = (file: File | undefined) => {
    if (!file || !map) return;
    if (file.size > 6 * 1024 * 1024) { fail(new Error("That picture is too big (6 MB at most)")); return; }
    const r = new FileReader();
    r.onload = () => void run(() => mapApi.upload(campaign.id, map.id, String(r.result), file.name.replace(/\.\w+$/, "")));
    r.readAsDataURL(file);
  };
  const price = data.pricePerImage;
  return (
    <section className="panel map-host">
      <h3>Map tools <span className="muted small">(Host and Co-hosts)</span></h3>
      {sel && (
        <div className="row map-selected">
          <strong>{sel.name}</strong>
          <span className="muted small">{squareLabel(sel.x, sel.y)}</span>
          <label className="check-inline"><input type="checkbox" checked={sel.hidden} disabled={busy} onChange={(e) => void run(() => mapApi.move(campaign.id, sel.id, { hidden: e.target.checked }))} /> Hidden from players</label>
          <label className="inline small">Size{" "}
            <select value={sel.size} disabled={busy} onChange={(e) => void run(() => mapApi.move(campaign.id, sel.id, { size: Number(e.target.value) }))}>
              <option value={1}>Medium or smaller</option><option value={2}>Large (2×2)</option><option value={3}>Huge (3×3)</option><option value={4}>Gargantuan (4×4)</option>
            </select>
          </label>
          {sel.kind === "npc" && <button type="button" className="link danger" disabled={busy} onClick={() => void run(() => mapApi.removeToken(campaign.id, sel.id)).then(() => setSelected(null))}>Remove</button>}
        </div>
      )}
      {map && (
        <>
          <div className="row">
            <label className="check-inline"><input type="checkbox" checked={map.fog} disabled={busy} onChange={(e) => void run(() => mapApi.update(campaign.id, map.id, { fog: e.target.checked }))} /> Fog of war</label>
            {map.fog && <button type="button" disabled={busy} onClick={() => void run(() => mapApi.fog(campaign.id, map.id, "revealAll"))}>Reveal all</button>}
            {map.fog && <button type="button" disabled={busy} onClick={() => void run(() => mapApi.fog(campaign.id, map.id, "coverAll"))}>Cover all</button>}
          </div>
          <div className="row map-walls-row">
            <span>Walls: {(map.blocked ?? []).length} squares <span className="muted small">(use the Walls / Erase tools above; monsters path around them and players can't stop on them)</span></span>
            {(map.blocked ?? []).length > 0 && <button type="button" className="link" disabled={busy} onClick={() => { if (confirm("Remove all walls from this map?")) void run(() => mapApi.walls(campaign.id, map.id, "clear")); }}>Clear walls</button>}
          </div>
          <div className="row">
            <label className="inline">Picture{" "}<input type="file" accept="image/png,image/jpeg,image/webp" disabled={busy} onChange={(e) => upload(e.target.files?.[0])} /></label>
            {map.imageId && <button type="button" className="link" disabled={busy} onClick={() => void run(() => mapApi.clearImage(campaign.id, map.id))}>Plain grid</button>}
            {map.imageStatus === "failed" && map.byDm && !map.imageId && <span className="muted small">The DM's picture couldn't be made (cost cap or an error): upload or generate one, or keep the plain grid.</span>}
          </div>
          {data.canGenerate && (
            <form className="row" onSubmit={(e) => { e.preventDefault(); if (prompt.trim()) void run(() => mapApi.generate(campaign.id, map.id, prompt)).then(() => setPrompt("")); }}>
              <input style={{ flex: 1, minWidth: 180 }} value={prompt} maxLength={700} disabled={busy} placeholder="Describe the place, e.g. a rope bridge over a gorge, rocks on both sides"
                onChange={(e) => setPrompt(e.target.value)} />
              <button type="submit" disabled={busy || prompt.trim().length < 3}>{busy ? "Working…" : "Make a map picture"}</button>
              {price !== null && <span className="muted small">about ${price.toFixed(2)} per picture (Grok Imagine)</span>}
            </form>
          )}
          <form className="row" onSubmit={(e) => { e.preventDefault(); if (npc.name.trim()) void run(() => mapApi.addToken(campaign.id, npc)).then(() => setNpc({ ...npc, name: "" })); }}>
            <input value={npc.name} maxLength={60} disabled={busy} placeholder="Add a token (e.g. Merchant, Cart)" onChange={(e) => setNpc({ ...npc, name: e.target.value })} />
            <select aria-label="Token size" value={npc.size} disabled={busy} onChange={(e) => setNpc({ ...npc, size: Number(e.target.value) })}>
              <option value={1}>1×1</option><option value={2}>2×2</option><option value={3}>3×3</option><option value={4}>4×4</option>
            </select>
            <label className="check-inline"><input type="checkbox" checked={npc.hidden} onChange={(e) => setNpc({ ...npc, hidden: e.target.checked })} /> Hidden</label>
            <button type="submit" disabled={busy || !npc.name.trim()}>Add</button>
          </form>
          <div className="row">
            <label className="inline small">Size{" "}
              <input type="number" aria-label="Columns" min={MAP_MIN_SIZE} max={MAP_MAX_SIZE} defaultValue={map.cols} key={`c${map.id}${map.cols}`} style={{ width: "4.5em" }}
                onBlur={(e) => Number(e.target.value) !== map.cols && void run(() => mapApi.update(campaign.id, map.id, { cols: Number(e.target.value) }))} />
              {" × "}
              <input type="number" aria-label="Rows" min={MAP_MIN_SIZE} max={MAP_MAX_SIZE} defaultValue={map.rows} key={`r${map.id}${map.rows}`} style={{ width: "4.5em" }}
                onBlur={(e) => Number(e.target.value) !== map.rows && void run(() => mapApi.update(campaign.id, map.id, { rows: Number(e.target.value) }))} />
              {" squares"}
            </label>
            <button type="button" className="link danger" disabled={busy}
              onClick={() => window.confirm(`Delete the map "${map.name}" and its tokens?`) && void run(() => mapApi.remove(campaign.id, map.id))}>Delete map</button>
          </div>
        </>
      )}
      {data.maps.length > 1 && (
        <label className="field inline">
          <span>Scene</span>
          <select value={map?.id ?? ""} disabled={busy} onChange={(e) => void run(() => mapApi.update(campaign.id, Number(e.target.value), { active: true }))}>
            {!map && <option value="">Pick a map</option>}
            {data.maps.map((m) => <option key={m.id} value={m.id}>{m.name} ({m.cols}×{m.rows})</option>)}
          </select>
        </label>
      )}
      <form className="row" onSubmit={(e) => { e.preventDefault(); void run(() => mapApi.create(campaign.id, { ...form, name: form.name.trim() || "Battle" })).then(() => setForm({ ...form, name: "" })); }}>
        <input value={form.name} maxLength={80} placeholder="New map name (e.g. Old Bridge)" onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <input type="number" aria-label="New map columns" min={MAP_MIN_SIZE} max={MAP_MAX_SIZE} value={form.cols} style={{ width: "4.5em" }} onChange={(e) => setForm({ ...form, cols: Number(e.target.value) })} />
        ×
        <input type="number" aria-label="New map rows" min={MAP_MIN_SIZE} max={MAP_MAX_SIZE} value={form.rows} style={{ width: "4.5em" }} onChange={(e) => setForm({ ...form, rows: Number(e.target.value) })} />
        <button type="submit" disabled={busy}>{map ? "New map" : "Make a map"}</button>
      </form>
      <p className="muted small">Each map keeps its own tokens and fog, so you can switch scenes and come back. The DM can move monsters and place creatures itself during play.</p>
    </section>
  );
}
